import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPurchasesReport } from '../api/distributionService';
import { PurchasesReport } from '../models/distribution';
import { formatDate, formatCurrency } from '../utils/formatters';

interface PurchasesDashboardProps {
  timeRange?: 'day' | 'week' | 'month' | 'year';
}

export default function PurchasesDashboard({ timeRange = 'month' }: PurchasesDashboardProps) {
  const { locationId } = useParams<{ locationId: string }>();
  const [report, setReport] = useState<PurchasesReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const now = new Date();
        let startDate = new Date();

        switch (timeRange) {
          case 'day':
            startDate.setDate(now.getDate() - 1);
            break;
          case 'week':
            startDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }

        const data = await getPurchasesReport(locationId!, startDate, now);
        setReport(data);
      } catch (err) {
        setError('Failed to load purchases data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (locationId) loadData();
  }, [locationId, timeRange]);

  if (loading) return <div className="p-4">Loading purchases data...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!report) return <div className="p-4">No purchases data available</div>;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard 
          title="Total Purchases" 
          value={report.total} 
          format={formatCurrency}
          icon="🛒"
        />
        <KPICard 
          title="Total Quantity" 
          value={report.data.reduce((sum, p) => sum + p.quantityKg, 0)} 
          format={(val) => `${val.toFixed(2)} kg`}
          icon="⛽"
        />
        <KPICard 
          title="Transactions" 
          value={report.count} 
          icon="📋"
        />
      </div>

      {/* Purchases List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold">Purchase History</h2>
          <p className="text-sm text-gray-500">
            Showing {report.count} purchases totaling {formatCurrency(report.total)}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <TableHeader>Date</TableHeader>
                <TableHeader>Batch #</TableHeader>
                <TableHeader>Quantity (kg)</TableHeader>
                <TableHeader>Unit Price</TableHeader>
                <TableHeader>Total Amount</TableHeader>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.data.map((purchase) => (
                <PurchaseTableRow key={purchase.id} data={purchase} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Sub-components
interface KPICardProps {
  title: string;
  value: number;
  format?: (value: number) => string;
  icon: string;
}

function KPICard({ title, value, format = (v) => v.toString(), icon }: KPICardProps) {
  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="mt-2 text-2xl font-semibold">
        {format(value)}
      </p>
    </div>
  );
}

function TableHeader({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

function PurchaseTableRow({ data }: { data: PurchasesReport['data'][0] }) {
  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(data.purchaseDate)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
        {data.batchNumber}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {data.quantityKg.toFixed(2)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatCurrency(data.pricePerKg)}/kg
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {formatCurrency(data.totalAmount)}
      </td>
    </tr>
  );
}